import React, { useState, useEffect, useRef } from 'react';
import { createFragmentContainer, graphql } from 'react-relay';
import PropTypes from 'prop-types';
import { useRouteMatch, useLocation } from 'react-router-dom';
import classNames from 'classnames';
import { useFlag } from '@unleash/proxy-client-react';

import {
  PRESALE_EVENT_OFF,
  MAIN_SEARCH_INACTIVE,
  MAIN_SEARCH,
  PRESALE,
  ALTERNATIVE,
  FACTOR_EMAIL,
  MAIN_SEARCH_COMBINED,
  FACTOR_CODE,
  MAIN_SEARCH_FACE,
  STRAVA
} from '../../../utils/variables';
import AdditionalSearchOptions from '../extended-search/AdditionalSearchOptions';
import PresaleEventInfo from './PresaleEventInfo';
import GDPRHint from '../GDPRHint';
import SecondFactorGuard from './SecondFactorGuard';
import { SecondFactorDetailsInfo } from './SecondFactorDetailsInfo';
import EventSearchOptions from '../extended-search/EventSearchOptions';
import { customScrollToComponent, defineActiveSearchType } from '../../../utils/common';
import InactiveEventInfo from './InactiveEventInfo';

////////////////////////////////////////////
// TODO MISSING TESTS - related to all 3 useEffects (Relay error)
////////////////////////////////////////////

const EventAvailableSearchActions = ({
  info,
  userLoginEmail,
  shouldShowAdditionalInstruction,
  startNumber,
  showHowToBuyHint,
  onShowHowToBuyHint,
  hasSmartSearchTab,
  searchStartnumber,
  codeSecondFactorForFaceEvent,
  setMainSearchFormRef,
  onInputChange,
  onSearchTrigger,
  onExtendedSearchHintClosed,
  secondFactor,
  showSearchInstructionsDialog,
  hasExtendedSearch,
  isAccessGrantedByValidCodeFaceEvent,
  setAltSearchRef,
  shouldShowMainSearch,
  goBackToMainSearchResults,
  elementToAnimate,
  isParticipant,
  hasDualSearchTab,
  searchTypeToForceShow,
  warnTextMessage,
  afterSearchMessage,
  showAfterSearchDialog,
  onAfterSearchDialogAccept,
}) => {
  const match = useRouteMatch();
  const location = useLocation();
  const extendedSearchRef = useRef(null);
  const searchOptionsContainerRef = useRef(null);

  const [selectedSearchType, setSelectedSearchType] = useState(
    defineActiveSearchType(match.url, hasEventPresale)
  );

  const isStravaSearchAvailable = useFlag('strava');

  const isMainEventPage = match.path === "/:language/event/:id";
  const hasEventInactiveSearch = info.mainSearch === MAIN_SEARCH_INACTIVE;

  const eventHasAltOptions =
  info.gps ||
  info.searchCollections ||
  hasDualSearchTab ||
  hasSmartSearchTab ||
  isStravaSearchAvailable;

  // presale
  const [showPresaleInfo, setShowPresaleInfo] = useState(false);
  const [presaleInfoWasShown, setPresaleInfoWasShown] = useState(false);
  const hasEventPresale = info.presale != PRESALE_EVENT_OFF;  
  const showMainSearchForPresale = (hasEventPresale && presaleInfoWasShown) || (hasEventPresale && !isMainEventPage);

  // second factor
  const [show2FAForInactive, setShow2FAForInactive] = useState(false);
  const hasEventSecondFactor = Boolean(info.secondFactorType?.type);
  const isFactorEmail = info.secondFactorType?.type === FACTOR_EMAIL;
  const isUserAuthenticatedForEmail = isFactorEmail &&  info.participantUser;
  const isUserAuthenticatedForNoEmail2FA = !isFactorEmail && isParticipant;
  const hasUserAccessToMainSearch = !hasEventSecondFactor || (hasEventSecondFactor && (isUserAuthenticatedForEmail || isUserAuthenticatedForNoEmail2FA));
  const showRightColumnFor2FA = hasEventSecondFactor && ((isFactorEmail && !isUserAuthenticatedForEmail) || (!isFactorEmail && !isUserAuthenticatedForNoEmail2FA));
  const show2FAForPresale = isFactorEmail && !isUserAuthenticatedForEmail && hasEventPresale && presaleInfoWasShown;
  const show2FAEmailFormForOnlineEVent = !hasUserAccessToMainSearch && !hasEventInactiveSearch && !hasEventPresale;
  const showMainSearchFormForNoEmail2FA = hasEventSecondFactor && !isFactorEmail && !isParticipant;

  // inactive event
  const shouldShowInitialInactiveMessage = hasEventInactiveSearch && selectedSearchType === MAIN_SEARCH && !show2FAForInactive;
  const showRightColumnForInactive2FA = hasEventInactiveSearch && isFactorEmail && !hasUserAccessToMainSearch
  const showRightColumnForInactiveNo2FA = hasEventInactiveSearch && !hasEventSecondFactor;
  const showMainSearchForInactive = ((hasEventInactiveSearch && isUserAuthenticatedForEmail) || (hasEventInactiveSearch && !hasEventSecondFactor)) && selectedSearchType != MAIN_SEARCH;
  const showRightColumnForInactiveAuthenticated = hasEventInactiveSearch && isFactorEmail && hasUserAccessToMainSearch;

  // right column visibility
  const showRightColumnForOnlineEvent = (hasEventSecondFactor && hasUserAccessToMainSearch) || !hasEventSecondFactor;
  const showColumnWithAltOptions = eventHasAltOptions && ((showRightColumnForOnlineEvent && !hasEventInactiveSearch) || (hasEventInactiveSearch && (showRightColumnForInactiveNo2FA || showRightColumnForInactiveAuthenticated)));
  const showColumnWith2FAInfo = (!hasEventInactiveSearch && showRightColumnFor2FA) || showRightColumnForInactive2FA;
  const showRightColumn =  showColumnWith2FAInfo || showColumnWithAltOptions;

  useEffect(() => {
    if (hasEventPresale && !presaleInfoWasShown && isMainEventPage) {
      setShowPresaleInfo(true);
      setSelectedSearchType(PRESALE);
    }
    if (presaleInfoWasShown) {
      setShowPresaleInfo(false);
    }
  }, [hasEventPresale, presaleInfoWasShown]);

  useEffect(() => {
    if (extendedSearchRef && setAltSearchRef) {
      setAltSearchRef(extendedSearchRef);
    }
  }, [extendedSearchRef, setAltSearchRef]);

  useEffect(() => {
    if (shouldShowMainSearch) {
      if (info.mainSearch === MAIN_SEARCH_COMBINED) {
        setSelectedSearchType(MAIN_SEARCH_COMBINED)
      } else setSelectedSearchType(MAIN_SEARCH);
    }
  }, [shouldShowMainSearch]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const authorizationStravaCode = searchParams.get('code');
    if (authorizationStravaCode) {
      setSelectedSearchType(STRAVA)
    }
  }, [])

  useEffect(() => {
    if (searchTypeToForceShow) {
      setSelectedSearchType(searchTypeToForceShow);
    }
  }, [searchTypeToForceShow]);

  const setSearchType = (type) => {
    setSelectedSearchType(type);
  };

  const goBackToMainSearchSlide = () => {
    if (hasEventPresale && !presaleInfoWasShown) {      
      setShowPresaleInfo(true);
      setSelectedSearchType(PRESALE);
    } else {
      setSelectedSearchType(MAIN_SEARCH);
    }
  };

  return (
    <>
      <div
        className={classNames('col-xs-16 mb-20', {
          'col-sm-8': !showRightColumn,
          'col-sm-7': showRightColumn,
        })}
        ref={searchOptionsContainerRef}
      >
        {/* ******************************** EVENT WITH NON-ACTIVE MAIN SEARCH (LEGACY) ******************************** */}
        {shouldShowInitialInactiveMessage && (
          <InactiveEventInfo
            eventInfo={info}
            onGoToAuthentication={() => {
              setShow2FAForInactive(true);
              setSelectedSearchType(MAIN_SEARCH);
            }}
          />
        )}
        {/* ******************************** PRESALE MESSAGE ******************************** */}
        {isMainEventPage &&
          showPresaleInfo &&
          selectedSearchType === PRESALE &&
          !show2FAForPresale && (
            <PresaleEventInfo
              eventPresaleInfo={info}
              onShowRegularSearch={() => {
                setPresaleInfoWasShown(true);
                if (selectedSearchType === PRESALE) {
                  setSelectedSearchType(MAIN_SEARCH);
                }
              }}
              warnTextMessage={warnTextMessage}
            />
          )}
        {/* ******************************** SECOND FACTOR (EMAIL) ******************************** */}
        {isFactorEmail &&
          (show2FAEmailFormForOnlineEVent ||
            show2FAForInactive ||
            (show2FAForPresale && !hasEventInactiveSearch)) &&
          selectedSearchType === MAIN_SEARCH && (
            <SecondFactorGuard
              eventSgId={info.sgId}
              mainSearch={info.mainSearch}
              setMainSearchFormRef={setMainSearchFormRef}
              shouldShowAdditionalInstruction={shouldShowAdditionalInstruction}
              userLoginEmail={userLoginEmail}
              showHowToBuyHint={showHowToBuyHint}
              onShowHowToBuyHint={onShowHowToBuyHint}
              warnTextMessage={warnTextMessage}
            />
          )}
        {/* ******************************** ALL SEARCH OPTIONS (MAIN, ALTERNATIVE) ******************************** */}
        {(!hasEventSecondFactor ||
          hasUserAccessToMainSearch ||
          showMainSearchFormForNoEmail2FA) &&
          selectedSearchType != PRESALE &&
          (!hasEventInactiveSearch || showMainSearchForInactive) && (
            <EventSearchOptions
              eventSearchOptionsInfo={info}
              activeSlideName={selectedSearchType}
              codeSecondFactorForFaceEvent={codeSecondFactorForFaceEvent}
              startNumber={startNumber}
              setMainSearchFormRef={setMainSearchFormRef}
              onInputChange={onInputChange}
              onSearchTrigger={onSearchTrigger}
              onExtendedSearchHintClosed={onExtendedSearchHintClosed}
              secondFactor={secondFactor}
              showSearchInstructionsDialog={showSearchInstructionsDialog}
              hasExtendedSearch={hasExtendedSearch}
              showHowToBuyHint={showHowToBuyHint}
              onShowHowToBuyHint={onShowHowToBuyHint}
              isAccessGrantedByValidCodeFaceEvent={isAccessGrantedByValidCodeFaceEvent}
              isAccessGrantedByValidCode={isParticipant && info.secondFactorType?.type === FACTOR_CODE}
              hasUserAccessToMainSearch={
                (hasUserAccessToMainSearch && !hasEventInactiveSearch) ||
                showMainSearchFormForNoEmail2FA
              }
              goBackToMainSearchSlide={goBackToMainSearchSlide}
              searchStartnumber={searchStartnumber}
              hasSmartSearchTab={hasSmartSearchTab}
              setSearchType={setSearchType}
              goBackToMainSearchResults={goBackToMainSearchResults}
              triggerPointingAnimation={elementToAnimate === MAIN_SEARCH}
              hasDualSearchTab={hasDualSearchTab}
              isCombinedSearchEvent={info.mainSearch === MAIN_SEARCH_COMBINED}
              warnTextMessage={(warnTextMessage && !presaleInfoWasShown) ? warnTextMessage : null}
              afterSearchMessage={afterSearchMessage}
              showAfterSearchDialog={showAfterSearchDialog}
              onAfterSearchDialogAccept={onAfterSearchDialogAccept}
            />
          )}
        <GDPRHint className="mt-15 mb-15 ml-15 mr-15" />
      </div>
      {showRightColumn && (
        <div className="col-xs-16 col-sm-5" ref={extendedSearchRef}>
          {/* ******************************** RIGHT FRAME ******************************** */}
          {!isParticipant && showColumnWith2FAInfo && <SecondFactorDetailsInfo />}
          {hasUserAccessToMainSearch && showColumnWithAltOptions && (
            <AdditionalSearchOptions
              hasSmartSearchTab={hasSmartSearchTab}
              hasCategorySearchTab={info.searchCollections}
              hasGpsSearchTab={info.gps}
              hasDualSearchTab={hasDualSearchTab}
              setSearchType={setSearchType}
              hasEventPresale={hasEventPresale}
              onScrollToSearchOptions={() =>
                searchOptionsContainerRef &&
                customScrollToComponent({currentRef: searchOptionsContainerRef.current})
              }
              triggerPointingAnimation={elementToAnimate === ALTERNATIVE}
              hasEventInactiveSearch={hasEventInactiveSearch}
              hasEventFaceSearch={info.mainSearch === MAIN_SEARCH_FACE}
              hasEventCombinedSearch={info.mainSearch === MAIN_SEARCH_COMBINED}
              selectedSearchType={selectedSearchType}
              isStravaSearchAvailable={isStravaSearchAvailable}
            />
          )}
        </div>
      )}
    </>
  );
};

EventAvailableSearchActions.propTypes = {
  info: PropTypes.object,
  userLoginEmail: PropTypes.string,
  shouldShowAdditionalInstruction: PropTypes.bool,
  startNumber: PropTypes.string,
  showHowToBuyHint: PropTypes.bool,
  onShowHowToBuyHint: PropTypes.func,
  hasSmartSearchTab: PropTypes.bool,
  searchStartnumber: PropTypes.string,
  codeSecondFactorForFaceEvent: PropTypes.string,
  setMainSearchFormRef: PropTypes.func,
  onInputChange: PropTypes.func,
  onSearchTrigger: PropTypes.func,
  onExtendedSearchHintClosed: PropTypes.func,
  secondFactor: PropTypes.string,
  showSearchInstructionsDialog: PropTypes.bool,
  hasExtendedSearch: PropTypes.bool,
  isAccessGrantedByValidCodeFaceEvent: PropTypes.bool,
  setAltSearchRef: PropTypes.func,
  shouldShowMainSearch: PropTypes.bool,
  goBackToMainSearchResults: PropTypes.func,
  elementToAnimate: PropTypes.string,
  isParticipant: PropTypes.bool,
  searchTypeToForceShow: PropTypes.string,
  onAfterSearchDialogAccept: PropTypes.func,
  showAfterSearchDialog: PropTypes.bool,
  warnTextMessage: PropTypes.string,
  afterSearchMessage: PropTypes.string,
};

export { EventAvailableSearchActions };
export default createFragmentContainer(EventAvailableSearchActions, {
  info: graphql`
    fragment EventAvailableSearchActions_info on Event {
      mainSearch
      sgId
      presaleMessage
      presale
      gps
      searchDual
      searchCollections
      participantUser
      secondFactorType {
        type
      }
      ...PresaleEventInfo_eventPresaleInfo
      ...EventSearchOptions_eventSearchOptionsInfo
      ...InactiveEventInfo_eventInfo
    }
  `,
});
